import { db } from "./firebase"
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  getDoc,
  setDoc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore"

// Admin user IDs - add your Firebase auth UIDs here
const ADMIN_USER_IDS = [
  "CvWOX0v1C4QBN2oHdLCZoNGnuVf2",
  "YKWCl65I5NOt7dL2UOI9DTAMGPL2",
]

export const isAdmin = (user) => {
  if (!user) return false
  return ADMIN_USER_IDS.includes(user.uid)
}

// Purchase status constants
export const PURCHASE_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  DENIED: "denied",
}

/**
 * Create a new purchase record and immediately deduct points
 */
export const createPurchase = async (userId, userName, item) => {
  try {
    // Get current user points
    const currentPoints = await getUserPoints(userId)

    // Check if user has enough points
    if (currentPoints < item.price) {
      throw new Error(
        `Insufficient points. You have ${currentPoints} but need ${item.price}`
      )
    }

    // Calculate new points after deduction
    const newPoints = currentPoints - item.price

    const purchase = {
      userId,
      userName,
      itemId: item.id,
      itemName: item.name,
      itemPrice: item.price,
      itemDescription: item.description,
      itemImage: item.image || null,
      status: PURCHASE_STATUS.PENDING,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      approvedBy: null,
      approvedAt: null,
      // Track point deduction for potential refund
      pointsDeducted: item.price,
      originalPoints: currentPoints,
      newPoints: newPoints,
    }

    // Create purchase record and deduct points atomically
    const docRef = await addDoc(collection(db, "purchases"), purchase)

    // Immediately deduct points
    await updateUserPoints(userId, newPoints)

    return { id: docRef.id, ...purchase }
  } catch (error) {
    throw error
  }
}

/**
 * Get user's purchase history
 */
export const getUserPurchases = async (userId) => {
  try {
    // Simple query without orderBy to avoid index issues
    const q = query(collection(db, "purchases"), where("userId", "==", userId))

    const querySnapshot = await getDocs(q)

    const purchases = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }))

    // Sort in JavaScript instead of Firestore
    purchases.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(0)
      const bTime = b.createdAt?.toDate?.() || new Date(0)
      return bTime - aTime // descending order (newest first)
    })

    return purchases
  } catch (error) {

    // Return empty array instead of throwing
    return []
  }
}

/**
 * Get all pending purchases (for admin)
 */
export const getPendingPurchases = async () => {
  try {
    // First try a simple query without orderBy to see if the collection exists
    const simpleQuery = query(
      collection(db, "purchases"),
      where("status", "==", PURCHASE_STATUS.PENDING)
    )

    const querySnapshot = await getDocs(simpleQuery)

    const purchases = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }))

    // Sort in JavaScript instead of Firestore to avoid index issues
    purchases.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(0)
      const bTime = b.createdAt?.toDate?.() || new Date(0)
      return aTime - bTime // ascending order
    })

    return purchases
  } catch (error) {

    // Return empty array instead of throwing to prevent app crash
    return []
  }
}

/**
 * Approve a purchase
 */
export const approvePurchase = async (purchaseId, adminUserId) => {
  try {
    const purchaseRef = doc(db, "purchases", purchaseId)
    await updateDoc(purchaseRef, {
      status: PURCHASE_STATUS.APPROVED,
      approvedBy: adminUserId,
      approvedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
  } catch (error) {
    throw error
  }
}

/**
 * Deny a purchase and refund points
 */
export const denyPurchase = async (purchaseId, adminUserId) => {
  try {
    const purchaseRef = doc(db, "purchases", purchaseId)

    // Get the purchase to check point deduction info
    const purchaseDoc = await getDoc(purchaseRef)
    if (!purchaseDoc.exists()) {
      throw new Error("Purchase not found")
    }

    const purchaseData = purchaseDoc.data()

    // Update purchase status
    await updateDoc(purchaseRef, {
      status: PURCHASE_STATUS.DENIED,
      approvedBy: adminUserId,
      approvedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })

    // Refund points if they were deducted
    if (purchaseData.pointsDeducted && purchaseData.pointsDeducted > 0) {
      const currentPoints = await getUserPoints(purchaseData.userId)
      const refundedPoints = currentPoints + purchaseData.pointsDeducted
      await updateUserPoints(purchaseData.userId, refundedPoints)
    }
  } catch (error) {
    throw error
  }
}

/**
 * Get or create user points record
 */
export const getUserPoints = async (userId) => {
  try {
    const userRef = doc(db, "userPoints", userId)
    const userDoc = await getDoc(userRef)

    if (userDoc.exists()) {
      return userDoc.data().points || 0
    } else {
      // Create new user with default points
      await setDoc(userRef, { points: 0, updatedAt: serverTimestamp() })
      return 0
    }
  } catch (error) {
    return 0
  }
}

/**
 * Update user points
 */
export const updateUserPoints = async (userId, newPoints) => {
  try {
    const userRef = doc(db, "userPoints", userId)
    // Use setDoc with merge to handle cases where document doesn't exist
    await setDoc(
      userRef,
      {
        points: newPoints,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    )
  } catch (error) {
    throw error
  }
}

// Store Configuration Functions

const STORE_CONFIG_DOC_ID = "storeConfiguration"

// Default store items configuration
export const DEFAULT_STORE_CONFIG = [
  {
    id: 1,
    name: "Slushie at RaceTrac",
    description: "Delicious frozen treat from RaceTrac",
    price: 30,
    icon: "LocalDrink",
    image: "/images/racetrac.png",
    rarity: "rare",
    category: "treats",
    inStock: true,
    featured: true,
  },
  {
    id: 2,
    name: "30 Minutes Screen Time",
    description: "Extra 30 minutes of gaming or video time",
    price: 5,
    icon: "ScreenShare",
    image: "/images/switch.png",
    rarity: "common",
    category: "privileges",
    inStock: true,
    featured: false,
  },
]

/**
 * Get store configuration from Firestore
 */
export const getStoreConfigFromFirestore = async () => {
  try {
    const storeConfigRef = doc(db, "settings", STORE_CONFIG_DOC_ID)
    const storeConfigSnap = await getDoc(storeConfigRef)

    if (storeConfigSnap.exists()) {
      const data = storeConfigSnap.data()
      return data.items || DEFAULT_STORE_CONFIG
    } else {
      // Initialize with default configuration if it doesn't exist
      await initializeDefaultStoreConfig()
      return DEFAULT_STORE_CONFIG
    }
  } catch (error) {
    // Fallback to default config
    return DEFAULT_STORE_CONFIG
  }
}

/**
 * Save store configuration to Firestore
 */
export const saveStoreConfigToFirestore = async (storeItems) => {
  try {
    const storeConfigRef = doc(db, "settings", STORE_CONFIG_DOC_ID)
    await setDoc(
      storeConfigRef,
      {
        items: storeItems,
        lastUpdated: serverTimestamp(),
        version: "1.0",
      },
      { merge: true }
    )

    return true
  } catch (error) {
    throw error
  }
}

/**
 * Initialize default store configuration in Firestore
 */
export const initializeDefaultStoreConfig = async () => {
  try {
    const storeConfigRef = doc(db, "settings", STORE_CONFIG_DOC_ID)
    await setDoc(storeConfigRef, {
      items: DEFAULT_STORE_CONFIG,
      lastUpdated: serverTimestamp(),
      version: "1.0",
      initialized: true,
    })

    return true
  } catch (error) {
    throw error
  }
}

/**
 * Migrate localStorage store config to Firestore (one-time migration helper)
 */
export const migrateLocalStorageToFirestore = async () => {
  try {
    // Try to get existing localStorage config
    const localConfig = localStorage.getItem("choreStoreConfig")
    if (localConfig) {
      const parsedConfig = JSON.parse(localConfig)
      await saveStoreConfigToFirestore(parsedConfig)
      return parsedConfig
    } else {
      // No local config, initialize with defaults
      await initializeDefaultStoreConfig()
      return DEFAULT_STORE_CONFIG
    }
  } catch (error) {
    // Initialize with defaults as fallback
    await initializeDefaultStoreConfig()
    return DEFAULT_STORE_CONFIG
  }
}
